const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const isVisible = (el: HTMLElement) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';

/** Keyboard-focusable elements inside `root`, in DOM order, skipping hidden ones. */
export function getFocusable(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => !el.hasAttribute('inert') && !el.closest('[inert]') && isVisible(el));
}

export function isFocusable(el: Element | null): el is HTMLElement {
  return !!el && el instanceof HTMLElement && el.matches(FOCUSABLE);
}

/** The element that really has focus, looking through open shadow roots. */
export function deepActiveElement(): Element | null {
  let el = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el;
}

/**
 * Keeps Tab / Shift+Tab inside a set of elements (used by the mobile sidebar).
 * Returns a cleanup function.
 */
export function trapFocus(container: HTMLElement, getElements: () => HTMLElement[]): () => void {
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const items = getElements();
    if (!items.length) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = deepActiveElement();
    if (event.shiftKey && (active === first || !items.includes(active as HTMLElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !items.includes(active as HTMLElement))) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKeydown);
  return () => container.removeEventListener('keydown', onKeydown);
}

let locks = 0;
let previous: { overflow: string; paddingRight: string } | null = null;

/** Prevents the page from scrolling behind a modal; calls are counted. */
export function lockScroll(): void {
  if (locks++ > 0) return;
  const html = document.documentElement;
  const scrollbar = window.innerWidth - html.clientWidth;
  previous = { overflow: html.style.overflow, paddingRight: document.body.style.paddingRight };
  html.style.overflow = 'hidden';
  if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
}

export function unlockScroll(): void {
  if (locks === 0 || --locks > 0) return;
  if (previous) {
    document.documentElement.style.overflow = previous.overflow;
    document.body.style.paddingRight = previous.paddingRight;
    previous = null;
  }
}
